import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Receipt, RefreshCw, Trash2, Edit3, ExternalLink, Search, Loader2, FileText, Plus, Download, TrendingUp, Wallet, PiggyBank, RefreshCcwDot, CheckCircle2 } from 'lucide-react';
import api, { adminReportsAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  PAID: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  AUTHORISED: 'bg-blue-100 text-blue-700 border-blue-200',
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  SENT: 'bg-amber-100 text-amber-700 border-amber-200',
  ACCEPTED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  DECLINED: 'bg-rose-100 text-rose-700 border-rose-200',
  VOIDED: 'bg-slate-100 text-slate-500 border-slate-200',
  DELETED: 'bg-slate-100 text-slate-500 border-slate-200',
};

export const AdminBilling = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editTarget, setEditTarget] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileSummary, setReconcileSummary] = useState(null);

  const reconcileAll = async () => {
    setReconciling(true);
    try {
      const r = await api.post('/xero/reconcile-all');
      const d = r.data || {};
      setReconcileSummary(d);
      const fixed = d.total_reconciled || 0;
      const custs = d.customers_with_updates || 0;
      toast.success(
        fixed
          ? `Reconciled ${fixed} payment${fixed === 1 ? '' : 's'} across ${custs} customer${custs === 1 ? '' : 's'} · ${d.stripe_marked_paid || 0} Stripe-marked · ${d.xero_backfilled || 0} back-filled`
          : `Sweep complete — everything already reconciled (${d.customers_processed || 0} customers checked)`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconcile All failed');
    } finally {
      setReconciling(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Pull from local cache (already includes both invoices and quotes)
      const r = await api.get('/xero/invoices/local');
      setItems(r.data || []);
    } catch (e) {
      // Fallback: try plain invoices endpoint
      try {
        const r2 = await api.get('/xero/invoices');
        setItems(Array.isArray(r2.data) ? r2.data : []);
      } catch {
        toast.error('Failed to load billing records');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Load the month-to-date finance summary widget independently of the table
  useEffect(() => {
    adminReportsAPI.billingSummary()
      .then((r) => setMonthSummary(r.data))
      .catch(() => setMonthSummary(null));
  }, []);

  const filtered = items.filter((i) => {
    if (docTypeFilter !== 'all' && (i.doc_type || 'invoice') !== docTypeFilter) return false;
    if (statusFilter !== 'all' && i.status !== statusFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (i.xero_invoice_number || '').toLowerCase().includes(s) ||
      (i.description || '').toLowerCase().includes(s) ||
      (i.customer_name || '').toLowerCase().includes(s)
    );
  });

  const handleSyncOne = async (item) => {
    setBusyId(item.id);
    try {
      const r = await api.post(`/xero/invoices/${item.id}/sync`);
      toast.success(`Synced ${item.xero_invoice_number || item.id} — status: ${r.data.status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item) => {
    const voidInXero = item.xero_invoice_id && (item.status === 'DRAFT' || item.doc_type === 'quote')
      ? confirm(`Also void/delete this in Xero? (Only DRAFT invoices and quotes can be voided.)`)
      : false;
    if (!confirm(`Remove ${item.xero_invoice_number || 'this record'} from Helios?${voidInXero ? ' Will also void in Xero.' : ''}`)) return;
    setBusyId(item.id);
    try {
      await api.delete(`/xero/invoices/${item.id}?void_in_xero=${voidInXero}`);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (item) => {
    setEditTarget(item);
    setEditAmount(item.amount || 0);
    setEditStatus(item.status || 'DRAFT');
    setEditDescription(item.description || '');
    setEditNotes(item.notes || '');
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await api.patch(`/xero/invoices/${editTarget.id}`, {
        description: editDescription,
        amount: parseFloat(editAmount) || 0,
        status: editStatus,
        notes: editNotes,
      });
      toast.success('Updated');
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const totalDue = filtered
    .filter((i) => (i.doc_type || 'invoice') !== 'quote' && i.status !== 'PAID' && i.status !== 'VOIDED' && i.status !== 'DELETED')
    .reduce((s, i) => s + (i.amount_due ?? i.amount ?? 0), 0);
  const totalPaid = filtered
    .filter((i) => i.status === 'PAID')
    .reduce((s, i) => s + (i.amount ?? 0), 0);

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Billing</h1>
            <p className="text-sm text-slate-500 mt-1">All Xero quotes & invoices · sync, edit, link, override, delete</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={reconcileAll}
              disabled={reconciling}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="admin-billing-reconcile-all"
            >
              {reconciling ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reconciling…</>
              ) : (
                <><RefreshCcwDot className="w-4 h-4 mr-2" /> Reconcile All</>
              )}
            </Button>
            <Button onClick={load} variant="outline" data-testid="admin-billing-reload">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {reconcileSummary && (
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="billing-reconcile-summary">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Reconciliation sweep complete
                {reconcileSummary.reconciled_at && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px]">
                    {formatDate(reconcileSummary.reconciled_at)}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {[
                { label: 'Customers checked', value: reconcileSummary.customers_processed || 0 },
                { label: 'Customers updated', value: reconcileSummary.customers_with_updates || 0 },
                { label: 'Payments reconciled', value: reconcileSummary.total_reconciled || 0 },
                { label: 'Marked paid', value: reconcileSummary.marked_paid || 0 },
                { label: 'Stripe-marked', value: reconcileSummary.stripe_marked_paid || 0 },
                { label: 'Xero back-filled', value: reconcileSummary.xero_backfilled || 0 },
              ].map((s) => (
                <div key={s.label} data-testid={`reconcile-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {monthSummary && (
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white" data-testid="billing-month-summary">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" /> This Month
                <Badge className="bg-blue-100 text-blue-700 border-blue-200 border text-[10px]">
                  {new Date(monthSummary.period_start).toLocaleString('en-GB', { month: 'long', year: 'numeric' })}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div data-testid="ms-total-billed">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Billed</p>
                <p className="text-2xl font-bold text-slate-900 mt-0.5">{formatCurrency(monthSummary.total_billed)}</p>
                <p className="text-[11px] text-slate-500">{monthSummary.invoice_count} invoice{monthSummary.invoice_count === 1 ? '' : 's'}</p>
              </div>
              <div data-testid="ms-avg-ticket">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Avg Ticket</p>
                <p className="text-2xl font-bold text-slate-900 mt-0.5">{formatCurrency(monthSummary.avg_ticket)}</p>
              </div>
              <div data-testid="ms-finance">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Finance</p>
                <p className="text-2xl font-bold text-emerald-700 mt-0.5">{formatCurrency(monthSummary.finance.amount)}</p>
                <p className="text-[11px] text-slate-500">{Math.round((monthSummary.finance.share || 0) * 100)}% · {monthSummary.finance.count} jobs</p>
              </div>
              <div data-testid="ms-cash">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><PiggyBank className="w-3 h-3" /> Cash</p>
                <p className="text-2xl font-bold text-blue-700 mt-0.5">{formatCurrency(monthSummary.cash.amount)}</p>
                <p className="text-[11px] text-slate-500">{Math.round((monthSummary.cash.share || 0) * 100)}% · {monthSummary.cash.count} jobs</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="billing-stat-due">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Outstanding</div>
              <div className="text-2xl font-bold text-amber-700 mt-1">{formatCurrency(totalDue)}</div>
            </CardContent>
          </Card>
          <Card data-testid="billing-stat-paid">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Collected</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1">{formatCurrency(totalPaid)}</div>
            </CardContent>
          </Card>
          <Card data-testid="billing-stat-count">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Records</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">{filtered.length}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="w-4 h-4" />
              Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Search by invoice #, description, customer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="billing-search"
                />
              </div>
              <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
                <SelectTrigger className="w-[140px]" data-testid="billing-doctype-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="invoice">Invoices</SelectItem>
                  <SelectItem value="quote">Quotes</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]" data-testid="billing-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="SENT">Sent</SelectItem>
                  <SelectItem value="AUTHORISED">Authorised</SelectItem>
                  <SelectItem value="ACCEPTED">Accepted</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="VOIDED">Voided</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="py-12 text-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin inline-block" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">No matching records.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
                      <th className="py-2 pr-3">Number</th>
                      <th className="py-2 pr-3">Type</th>
                      <th className="py-2 pr-3">Customer</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3 text-right">Amount</th>
                      <th className="py-2 pr-3">Created</th>
                      <th className="py-2 pr-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((it) => (
                      <tr key={it.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`billing-row-${it.xero_invoice_number || it.id}`}>
                        <td className="py-2 pr-3 font-medium">
                          <button
                            onClick={() => navigate(`/customers/${it.customer_id}`)}
                            className="text-blue-600 hover:underline"
                          >
                            {it.xero_invoice_number || '(no number)'}
                          </button>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant="outline" className="capitalize">{it.doc_type || 'invoice'}</Badge>
                            {it.invoice_type === 'variation' && (
                              <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px]">Variation</Badge>
                            )}
                            {it.invoice_type === 'deposit' && (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">Deposit</Badge>
                            )}
                            {it.invoice_type === 'final_payment' && (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Final</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-slate-700 truncate max-w-[180px]">
                          {it.customer_name || it.customer_id?.slice(0, 8)}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge className={`${STATUS_COLORS[it.status] || 'bg-slate-100 text-slate-700'} border`}>
                            {it.status}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-right font-medium">{formatCurrency(it.amount || 0)}</td>
                        <td className="py-2 pr-3 text-slate-500">{it.created_at ? formatDate(it.created_at) : '-'}</td>
                        <td className="py-2 pr-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {it.xero_invoice_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSyncOne(it)}
                                disabled={busyId === it.id}
                                title="Re-sync from Xero"
                                data-testid={`sync-${it.xero_invoice_number}`}
                              >
                                {busyId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(it)}
                              title="Edit"
                              data-testid={`edit-${it.xero_invoice_number}`}
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                              onClick={() => handleDelete(it)}
                              disabled={busyId === it.id}
                              title="Delete"
                              data-testid={`delete-${it.xero_invoice_number}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-billing-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editTarget?.doc_type === 'quote' ? 'Quote' : 'Invoice'} {editTarget?.xero_invoice_number}</DialogTitle>
            <DialogDescription>
              These changes are stored in Helios only — they do <strong>not</strong> push back to Xero. After fixing in Xero, click Sync (↻) to pull the latest values.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Description</Label>
              <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} data-testid="edit-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount</Label>
                <Input type="number" step="0.01" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} data-testid="edit-amount" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editStatus} onValueChange={setEditStatus}>
                  <SelectTrigger data-testid="edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['DRAFT', 'SENT', 'AUTHORISED', 'ACCEPTED', 'DECLINED', 'PAID', 'VOIDED', 'DELETED'].map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Internal notes (optional)" data-testid="edit-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>Cancel</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleSaveEdit}
              disabled={saving}
              data-testid="confirm-billing-edit"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminBilling;
